import { useState, useEffect, useRef } from "react";
import { X, ChevronDown, UserCircle } from "lucide-react";
import PersonAvatar from "@common-components/tasks/person-avatar/person-avatar";
import { userDisplayName } from "@common-models/user";
import { usePortalContainer } from "@/common/hooks/use-portal-container";
import { Badge } from "@/components/ui/badge";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

type MultiselectFilterValueProps = {
    options: any[];
    onSelectionChange?: (newValue: any[]) => void;
    valueField?: any[];
    isPerson?: boolean;
    isDisableTempId?: boolean;
};

export const MultiselectFilterValue = ({ options, onSelectionChange, valueField, isPerson, isDisableTempId }: MultiselectFilterValueProps) => {
    const portalContainer = usePortalContainer();
    const initialValueField = Array.isArray(valueField) ? valueField : [];
    const [value, setValue] = useState<any[]>(initialValueField);
    const [transformedOptions, setTransformedOptions] = useState<any[]>([]);
    const [filterText, setFilterText] = useState("");
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const transformed = options.map(option => {
            if (typeof option === 'object' && !isPerson && !option?.isGroup) {
                const id = Object.keys(option).filter(k => k !== 'optionColor')[0];
                const label = option[id];
                const optionColor = option.optionColor;
                return { id, label, optionColor };
            } else if (option?.isGroup) {
                const id = option.id
                const label = option.value
                const profile = isPerson ? option?.profile : undefined
                return { id, label, profile };
            } else if (isPerson) {
                const id = option.id
                const label = userDisplayName(option)
                const profile = option?.profile 
                return { id, label, profile };
            } else {
                return option;
            }
        });
        if (isPerson) {
            transformed.unshift('Blank');
        }
        setTransformedOptions(transformed);
    }, [isPerson, options]);

    useEffect(() => {
        setValue(Array.isArray(valueField) ? valueField : []);
    }, [valueField]);

    const isSelected = (opt: any) => {
        const id = typeof opt === 'object' ? opt.id : opt;
        return value.some(v => {
            if (typeof v === 'object' && v !== null) {
                return v.id === id;
            }
            return v === id;
        });
    };

    const toggleOption = (opt: any) => {
        const id = typeof opt === 'object' ? opt.id : opt;
        let newValue;
        if (isSelected(opt)) {
            newValue = value.filter(v => (typeof v === 'object' ? v.id : v) !== id);
        } else {
            newValue = [...value, opt];
        }
        setValue(newValue);
        onSelectionChange?.(newValue);
    };

    const handleRemove = (opt: any, e: React.MouseEvent) => {
        e.stopPropagation();
        const id = typeof opt === 'object' ? opt.id : opt;
        const newValue = value.filter(v => (typeof v === 'object' ? v.id : v) !== id);
        setValue(newValue);
        onSelectionChange?.(newValue);
    };

    const filteredOptions = transformedOptions.filter(opt => {
        const label = (typeof opt === 'object' ? opt.label : opt)?.toString().toLowerCase() || "";
        return label.includes(filterText.toLowerCase());
    });

    const getLabelContent = (option: any) => {
        if (option === 'Blank') return 'Blank';
        
        const isOptObject = typeof option === 'object';
        const labelText = isOptObject ? option.label : option;
        const color = isOptObject ? option.optionColor : null;

        return (
            <div className="flex items-center gap-2 truncate">
                {color && (
                    <div 
                        className="h-1.5 w-1.5 rounded-full shrink-0" 
                        style={{ backgroundColor: color }} 
                    />
                )}
                {isPerson && isOptObject && (
                    <PersonAvatar 
                        size={16} 
                        firstName={option?.profile?.firstName}
                        lastName={option?.profile?.lastName}
                        email={option?.profile?.email || option?.label} 
                        profileImg={option?.profile?.profileImg}  
                    />
                )}
                <span className="truncate">{labelText || 'Blank'}</span>
            </div>
        );
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <div 
                    className={cn(
                        "flex min-h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm hover:bg-accent/50 cursor-pointer transition-colors",
                        isDisableTempId && "opacity-50 cursor-not-allowed pointer-events-none"
                    )}
                >
                    <div className="flex flex-wrap gap-1 items-center overflow-hidden py-0.5">
                        {value.length > 0 ? (
                            value.map((val, i) => (
                                <Badge 
                                    key={i} 
                                    variant="secondary" 
                                    className="h-6 flex items-center gap-1.5 px-1.5 font-normal bg-accent border-none max-w-[120px]"
                                >
                                    {getLabelContent(val)}
                                    <X 
                                        className="h-3 w-3 opacity-60 hover:opacity-100 transition-opacity" 
                                        onClick={(e) => handleRemove(val, e)} 
                                    />
                                </Badge>
                            ))
                        ) : (
                            <span className="text-muted-foreground">Select...</span>
                        )}
                    </div>
                    <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
                </div>
            </PopoverTrigger>
            <PopoverContent 
                className="w-64 p-0 shadow-lg border-muted" 
                align="start"
                container={portalContainer}
            >
                <div className="p-2 border-b">
                    <Input 
                        placeholder="Search..." 
                        value={filterText} 
                        onChange={(e) => setFilterText(e.target.value)}
                        className="h-8 text-xs bg-muted/30 focus-visible:ring-1"
                    />
                </div>
                <ScrollArea className="max-h-60 overflow-y-auto">
                    <div className="p-1">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt, i) => (
                                <div 
                                    key={i} 
                                    className={cn(
                                        "flex items-center gap-2 px-2 py-1.5 hover:bg-accent rounded-sm cursor-pointer text-sm transition-colors",
                                        isSelected(opt) && "bg-accent/50"
                                    )}
                                    onClick={() => toggleOption(opt)}
                                >
                                    <Checkbox checked={isSelected(opt)} id={`opt-${i}`} />
                                    <div className="flex-1 min-w-0">
                                        {getLabelContent(opt)}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="py-6 text-center text-xs text-muted-foreground">
                                No results found
                            </div>
                        )}
                    </div>
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
};
